import React, { FC, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTooltip } from '../../../hooks/tooltip/useToolTip';
import styles from './SystemTooltip.module.css';
import { SystemTooltipVariant, Position, SystemTooltipProps } from './types';

const SystemTooltip: FC<SystemTooltipProps> = ({
  children,
  content,
  variant = SystemTooltipVariant.TOP_CENTER,
  delay = 300,
  disabled = false,
  className = '',
  tooltipClassName = '',
  offset = 4,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  const { showTooltip, handleMouseEnter, handleMouseLeave } = useTooltip(delay);

  // Calculate tooltip position with collision detection
  const calculatePosition = (): Position => {
    if (!triggerRef.current || !tooltipRef.current) {
      return { top: 0, left: 0, actualVariant: variant };
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let top = 0;
    let left = 0;
    let actualVariant = variant;

    // Calculate initial position based on preferred variant
    switch (variant) {
      case SystemTooltipVariant.TOP_LEFT:
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left;
        break;
      case SystemTooltipVariant.TOP_CENTER:
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case SystemTooltipVariant.TOP_RIGHT:
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.right - tooltipRect.width;
        break;
      default:
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
    }

    // Collision detection and adjustment
    const padding = 16; // Minimum distance from viewport edges

    // Check horizontal boundaries
    if (left < padding) {
      // Too far left, adjust to left alignment
      left = triggerRect.left;
      actualVariant = SystemTooltipVariant.TOP_LEFT;
    } else if (left + tooltipRect.width > viewport.width - padding) {
      // Too far right, adjust to right alignment
      left = triggerRect.right - tooltipRect.width;
      actualVariant = SystemTooltipVariant.TOP_RIGHT;
    }

    // Check if tooltip goes above viewport
    if (top < padding) {
      // If no space above, position below the trigger
      top = triggerRect.bottom + offset;
    }

    // Final check to ensure tooltip doesn't go off-screen
    left = Math.max(
      padding,
      Math.min(left, viewport.width - tooltipRect.width - padding)
    );
    top = Math.max(padding, top);

    return { top, left, actualVariant };
  };

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (showTooltip && !disabled) {
      // Small delay to ensure tooltip is rendered and has dimensions
      const timer = setTimeout(() => {
        const newPosition = calculatePosition();
        setPosition(newPosition);
      }, 0);

      // Handle window resize and scroll
      const handlePositionUpdate = () => {
        const newPosition = calculatePosition();
        setPosition(newPosition);
      };

      window.addEventListener('resize', handlePositionUpdate);
      window.addEventListener('scroll', handlePositionUpdate, true);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', handlePositionUpdate);
        window.removeEventListener('scroll', handlePositionUpdate, true);
      };
    }
    return undefined;
  }, [showTooltip, disabled, variant, offset]);

  const onMouseEnter = () => {
    if (!disabled) {
      handleMouseEnter();
    }
  };

  const onMouseLeave = () => {
    if (!disabled) {
      handleMouseLeave();
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={`${styles['system-tooltip__trigger']} ${className}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onMouseEnter}
        onBlur={onMouseLeave}
      >
        {children}
      </div>

      {showTooltip &&
        !disabled &&
        content &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`${styles['system-tooltip__content']} ${tooltipClassName}`}
            style={{
              position: 'fixed',
              top: position?.top || 0,
              left: position?.left || 0,
              zIndex: 9999,
              opacity: position ? 1 : 0,
              transition: 'opacity 0.2s ease-in-out',
            }}
            role='tooltip'
            aria-hidden={!showTooltip}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

export default SystemTooltip;
export { SystemTooltipVariant };
